import type { z } from "zod";
import type { DataSession } from "../../../data/core/session";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { userMeWireSchema } from "@libs/wire";

export type UserMeQueryData = RouterOutputs["user"]["me"];

/**
 * Compile-time drift check, bidirectional. The same instance of
 * `userMeWireSchema` is the server's `.output(...)` validator AND the
 * client cache's read/write validator. Both ends import it directly, so
 * runtime drift is impossible. The assertion below additionally guards
 * against any subtle shape-narrowing in the router output type vs the
 * schema (e.g. tRPC's transform layer drops a property).
 *
 * Forward direction: schema is missing fields the router returns.
 * Backward direction: schema is narrower than the router output (which
 * would cause runtime parsing to reject valid server data and pin the
 * cache to stale rows).
 */
type _UserMeWireSchemaSubset =
  z.infer<typeof userMeWireSchema> extends UserMeQueryData ? true : never;
type _UserMeWireSchemaSuperset =
  UserMeQueryData extends z.infer<typeof userMeWireSchema> ? true : never;
const _userMeWireSchemaDriftCheck: [
  _UserMeWireSchemaSubset,
  _UserMeWireSchemaSuperset,
] = [true, true];
void _userMeWireSchemaDriftCheck;

/**
 * Query key for `user.me`. Function form for symmetry with
 * `libraryQueryKey(uid)`. No uid prefix here — `useMe` is the source of
 * uid; prefixing would be circular.
 */
export const meQueryKey = (): readonly unknown[] => getQueryKey(trpc.user.me);

export const meQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    queryKey: meQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("meQuery queryFn");
      }
      return data.api.user.me.query();
    },
    schema: userMeWireSchema,
  });
